import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { DSTableProps } from "@/src/design_system/data_display/table/types/props";
import type { DSTableLayout } from "@/src/design_system/data_display/table/composables/useDSTableLayout";

export const useDSTableStickyPositioning = (props: DSTableProps, layout: DSTableLayout) => {
  const tableRef = ref<HTMLTableElement>();
  const columnWidths = ref<number[]>([]);
  const groupHeight = ref(0);
  const headerTop = computed(() => (props.stickyHeader ? `${groupHeight.value}px` : undefined));
  let resizeObserver: ResizeObserver | undefined;

  const stickyStyle = (index: number, endIndex = index) => {
    const widths = columnWidths.value;
    if (layout.isStickyStart(index)) {
      const offset = widths.slice(0, index).reduce((sum, width) => sum + width, 0);
      return { insetInlineStart: `${offset}px` };
    }
    if (layout.isStickyEnd(index)) {
      const offset = widths.slice(endIndex + 1).reduce((sum, width) => sum + width, 0);
      return { insetInlineEnd: `${offset}px` };
    }
    return {};
  };
  const measureColumnWidths = (table: HTMLTableElement) => {
    const widths = Array.from(
      table.querySelectorAll<HTMLTableCellElement>("thead tr:last-child th"),
      cell => cell.getBoundingClientRect().width
    );
    if (
      widths.some((width, index) => width !== columnWidths.value[index]) ||
      widths.length !== columnWidths.value.length
    )
      columnWidths.value = widths;
  };
  const measureGroupHeight = (table: HTMLTableElement) => {
    const firstRow = table.tHead?.rows[0];
    groupHeight.value = props.headerGroups?.length && firstRow ? firstRow.getBoundingClientRect().height : 0;
  };
  const measureHeaderDimensions = () => {
    const table = tableRef.value;
    if (!table) return;
    measureColumnWidths(table);
    measureGroupHeight(table);
  };
  const observeHeaderCells = () => {
    resizeObserver?.disconnect();
    measureHeaderDimensions();
    if (!resizeObserver || !tableRef.value) return;
    tableRef.value.querySelectorAll("thead th").forEach(cell => resizeObserver?.observe(cell));
  };
  onMounted(() => {
    if (typeof ResizeObserver !== "undefined") resizeObserver = new ResizeObserver(measureHeaderDimensions);
    void observeHeaderCells();
  });
  watch([layout.displayedColumns, layout.displayedGroups], observeHeaderCells, { flush: "post" });
  onBeforeUnmount(() => resizeObserver?.disconnect());

  return { tableRef, headerTop, stickyStyle };
};
